import { canEditSection } from '@/lib/access';
import React, { useEffect } from 'react';
import { useManualForm, FormShell, NumberField, TextAreaField, RepeatTable } from './_shared';
import { Card } from '@/components/UI';
import api from '@/lib/api';
import { useCycle } from '@/lib/cycle';

const DEFAULT = {
  summary: { total_suppliers_evaluated: 0, suppliers_above_target: 0, total_ncrs_raised: 0, rm_inventory_value: 0, fg_inventory_value: 0, wip_value: 0, inventory_turn_ratio: 0, inventory_turn_target: 19, notes: '' },
  supplier_ncrs: [],
  supplier_ratings: [],
  plating_returns: [],
};

const NCR_COLS = [
  { key: 'supplier', label: 'Supplier' },
  { key: 'material', label: 'Material' },
  { key: 'issue', label: 'Issue', type: 'textarea' },
  { key: 'qty_rejected', label: 'Qty', type: 'number', right: true },
  { key: 'qty_units', label: 'Units' },
  { key: 'resolution', label: 'Resolution' },
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'status', label: 'Status', type: 'select', options: ['open', 'closed'] },
];
const RATE_COLS = [
  { key: 'supplier', label: 'Supplier' },
  { key: 'quality_score', label: 'Quality', type: 'number', right: true },
  { key: 'delivery_score', label: 'Delivery', type: 'number', right: true },
  { key: 'overall_score', label: 'Overall', type: 'number', right: true },
  { key: 'grade', label: 'Grade', type: 'select', options: ['A', 'B', 'C', 'D'] },
  { key: 'remarks', label: 'Remarks' },
];
const PLATING_COLS = [
  { key: 'part_name', label: 'Part Name', type: 'readonly' },
  { key: 'part_no', label: 'Part No', type: 'readonly' },
  { key: 'returned_disp', label: 'Returned (pcs)', type: 'readonly', right: true },
  { key: 'plater', label: 'Plating Vendor (manual)' },
];

export default function SupplyChain() {
  const ctx = useManualForm('supply_chain', DEFAULT);
  const { currentUnit } = useCycle();
  const canEdit = canEditSection(ctx.user, 'supply_chain');
  const locked = ctx.currentCycle?.status === 'locked';
  const s = ctx.data.summary || DEFAULT.summary;
  const set = (k, v) => ctx.setData({ ...ctx.data, summary: { ...s, [k]: v } });
  const setKey = (k, rows) => ctx.setData({ ...ctx.data, [k]: rows });

  // Auto-fill the top-5 return-to-plating parts from the production JSON.
  // Part + qty are read-only; the plater is manual and preserved across refreshes.
  useEffect(() => {
    if (ctx.loading || !ctx.currentCycle || !currentUnit) return;
    let active = true;
    api.get(`/data/production/${ctx.currentCycle.year}/${ctx.currentCycle.month}?unit=${currentUnit.code}`)
      .then((r) => {
        if (!active || !r.data?.has_data) return;
        const top = r.data.data?.quality?.top_return_plating || [];
        const prev = ctx.data.plating_returns || [];
        const platerBy = {};
        prev.forEach((p) => { platerBy[p.part_no || p.part_name] = p.plater; });
        const next = top.map((x) => {
          let pctTxt = ' (—)';
          if (x.return_pct !== null && x.return_pct !== undefined) {
            pctTxt = ` (${x.return_pct}%${x.base_source === 'schedule' ? ' sch' : ''})`;
          }
          return {
            part_name: x.part_name,
            part_no: x.part_no,
            qty: x.qty,
            produced: x.produced,
            return_pct: x.return_pct,
            base_source: x.base_source,
            returned_disp: `${(x.qty || 0).toLocaleString('en-IN')}${pctTxt}`,
            plater: platerBy[x.part_no || x.part_name] || '',
          };
        });
        const sig = (arr) => JSON.stringify(arr.map((p) => [p.part_no, p.qty]));
        if (prev.length === 0 || sig(prev) !== sig(next)) {
          ctx.setData({ ...ctx.data, plating_returns: next });
        }
      })
      .catch(() => {});
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.loading, ctx.currentCycle?.id, currentUnit?.code]);

  return (
    <FormShell title="Supply Chain & Procurement" subtitle="Supplier performance, NCRs, inventory" status={ctx.status} save={ctx.save} submit={ctx.submit} saving={ctx.saving} submitting={ctx.submitting} canEdit={canEdit} locked={locked}>
      <Card title="Summary">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NumberField label="Suppliers Evaluated" value={s.total_suppliers_evaluated} onChange={(v) => set('total_suppliers_evaluated', v)} disabled={!canEdit || locked} />
          <NumberField label="Suppliers Above Target" value={s.suppliers_above_target} onChange={(v) => set('suppliers_above_target', v)} disabled={!canEdit || locked} />
          <NumberField label="NCRs Raised" value={s.total_ncrs_raised} onChange={(v) => set('total_ncrs_raised', v)} disabled={!canEdit || locked} />
          <NumberField label="RM Inventory (₹)" value={s.rm_inventory_value} onChange={(v) => set('rm_inventory_value', v)} disabled={!canEdit || locked} />
          <NumberField label="FG Inventory (₹)" value={s.fg_inventory_value} onChange={(v) => set('fg_inventory_value', v)} disabled={!canEdit || locked} />
          <NumberField label="WIP Value (₹)" value={s.wip_value} onChange={(v) => set('wip_value', v)} disabled={!canEdit || locked} />
          <NumberField label="Inventory Turn Ratio" value={s.inventory_turn_ratio} onChange={(v) => set('inventory_turn_ratio', v)} disabled={!canEdit || locked} />
          <NumberField label="Turn Target" value={s.inventory_turn_target} onChange={(v) => set('inventory_turn_target', v)} disabled={!canEdit || locked} />
        </div>
        <div className="mt-3"><TextAreaField label="Notes" value={s.notes} onChange={(v) => set('notes', v)} disabled={!canEdit || locked} /></div>
      </Card>
      <Card title="Supplier NCRs"><RepeatTable rows={ctx.data.supplier_ncrs || []} columns={NCR_COLS} onChange={(rows) => setKey('supplier_ncrs', rows)} addLabel="Add NCR" defaultRow={{ status: 'open' }} /></Card>
      <Card title="Supplier Ratings"><RepeatTable rows={ctx.data.supplier_ratings || []} columns={RATE_COLS} onChange={(rows) => setKey('supplier_ratings', rows)} addLabel="Add Supplier" defaultRow={{ grade: 'B' }} /></Card>
      <Card title="Top Plating Returns" action={<span className="text-xs text-slate-500">Top 5 auto from Production JSON • % of production ("sch" = vs schedule, part not produced in-house) • add the plater</span>}>
        <RepeatTable rows={ctx.data.plating_returns || []} columns={PLATING_COLS} onChange={(rows) => setKey('plating_returns', rows)} addLabel="Add Row" defaultRow={{ part_name: '', part_no: '', qty: 0, plater: '' }} />
      </Card>
    </FormShell>
  );
}
